/**
 * TwixT (24×24) の TypeScript 表現 (mixed-engine の serde 形と一致)。
 *
 * - board[y][x] = 'Empty' | 'Black' | 'White' (row-major、 (x, y) は (col, row))
 * - links: 自他両者のリンク (owner で区別)
 * - 黒 (First) = 上下辺、 白 (Second) = 左右辺
 * - Swap は後手 2 手目限定 (= move_number==1 && swap_available && side==Second)
 */

export type Side = 'First' | 'Second';
export type TwixtCell = 'Empty' | 'Black' | 'White';

export interface TwixtLink {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  owner: Side;
}

export type TwixtEndKind = 'Connection' | 'DrawTrivial' | 'DrawAlgorithmic';

export interface TwixtOutcome {
  winner: Side | null;
  kind: TwixtEndKind;
}

export interface TwixtState {
  board: TwixtCell[][];
  links: TwixtLink[];
  side: Side;
  move_number: number;
  swap_available: boolean;
  outcome: TwixtOutcome | null;
}

export type TwixtMove = 'Swap' | { Place: { x: number; y: number } };

export const TWIXT_SIZE = 24;

export function isPlace(m: TwixtMove): m is { Place: { x: number; y: number } } {
  return typeof m === 'object' && 'Place' in m;
}

export function legalKey(x: number, y: number): string {
  return `${x}-${y}`;
}

/** Swap が legal 集合に含まれるか。 */
export function hasSwap(legal: TwixtMove[] | undefined): boolean {
  return (legal ?? []).some((m) => m === 'Swap');
}

/** `(x, y)` が四隅 (= 配置不可) か。 */
export function isCorner(x: number, y: number): boolean {
  return (
    (x === 0 && y === 0) ||
    (x === 0 && y === TWIXT_SIZE - 1) ||
    (x === TWIXT_SIZE - 1 && y === 0) ||
    (x === TWIXT_SIZE - 1 && y === TWIXT_SIZE - 1)
  );
}

/** 自陣辺かどうか (= side が touch すべき辺)。 */
export function isOwnEdge(side: Side, x: number, y: number): boolean {
  if (side === 'First') {
    return y === 0 || y === TWIXT_SIZE - 1;
  }
  return x === 0 || x === TWIXT_SIZE - 1;
}

/** side が `(x, y)` に置けない (= 相手陣辺 or 角) かどうか。 */
export function isForbiddenFor(side: Side, x: number, y: number): boolean {
  if (isCorner(x, y)) return true;
  if (side === 'First') {
    return x === 0 || x === TWIXT_SIZE - 1;
  }
  return y === 0 || y === TWIXT_SIZE - 1;
}
